import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { CountryScore } from '@/services/scoreService';
import { getStatus, getStatusColor, getStatusLabel } from '@/data/regulatoryData';
import { FlagIcon } from '@/components/FlagIcon';

interface InlineSearchProps {
  jurisdictions: CountryScore[];
  onSelect: (jurisdiction: CountryScore) => void;
}

export interface InlineSearchHandle {
  focus: () => void;
}

/**
 * Always-visible inline search with dropdown results.
 * Shows flags, status badges, and scores for each jurisdiction.
 */
const InlineSearch = forwardRef<InlineSearchHandle, InlineSearchProps>(
  ({ jurisdictions, onSelect }, ref) => {
    const [search, setSearch] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose focus method to parent
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    // Close dropdown on escape
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isFocused) {
          setSearch('');
          inputRef.current?.blur();
          setIsFocused(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFocused]);

    // Group jurisdictions by type and sort alphabetically
    const groupedJurisdictions = useMemo(() => {
      const countries: CountryScore[] = [];
      const usStates: CountryScore[] = [];
      const caProvinces: CountryScore[] = [];

      jurisdictions.forEach((j) => {
        if (j.code.startsWith('US-')) {
          usStates.push(j);
        } else if (j.code.startsWith('CA-')) {
          caProvinces.push(j);
        } else {
          countries.push(j);
        }
      });

      // Sort each group alphabetically by name
      const sortByName = (a: CountryScore, b: CountryScore) => a.name.localeCompare(b.name);
      countries.sort(sortByName);
      usStates.sort(sortByName);
      caProvinces.sort(sortByName);

      return { countries, usStates, caProvinces };
    }, [jurisdictions]);

    const handleSelect = (jurisdiction: CountryScore) => {
      onSelect(jurisdiction);
      setSearch('');
      setIsFocused(false);
      inputRef.current?.blur();
    };

    const showDropdown = isFocused && (search.length > 0 || jurisdictions.length > 0);

    return (
      <div className="relative w-56">
        <Command
          className="glass rounded-lg shadow-glass overflow-visible"
          shouldFilter={true}
          loop
        >
          {/* Search Input */}
          <div className="flex items-center gap-2 px-3 py-2">
            <Search className="h-4 w-4 text-text-tertiary flex-shrink-0" />
            <Command.Input
              ref={inputRef}
              value={search}
              onValueChange={setSearch}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                // Delay to allow click on results
                setTimeout(() => {
                  if (!e.currentTarget.contains(document.activeElement)) {
                    setIsFocused(false);
                  }
                }, 150);
              }}
              placeholder="Search countries..."
              className="flex-1 bg-transparent border-0 outline-none text-sm text-text-primary placeholder:text-text-tertiary min-w-0"
            />
            <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-text-tertiary text-[10px] flex-shrink-0">
              /
            </kbd>
          </div>

          {/* Dropdown Results */}
          {showDropdown && (
            <Command.List className="absolute top-full right-0 mt-1 w-72 glass-strong rounded-lg shadow-card-elevated max-h-64 overflow-y-auto z-[1100]">
              <Command.Empty className="py-4 text-center text-xs text-text-secondary">
                No jurisdictions found.
              </Command.Empty>

              {/* Countries */}
              {groupedJurisdictions.countries.length > 0 && (
                <Command.Group>
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-tertiary font-medium border-b border-white/5">
                    Countries
                  </p>
                  {groupedJurisdictions.countries.map((jurisdiction) => (
                    <JurisdictionItem
                      key={jurisdiction.code}
                      jurisdiction={jurisdiction}
                      onSelect={() => handleSelect(jurisdiction)}
                    />
                  ))}
                </Command.Group>
              )}

              {/* US States */}
              {groupedJurisdictions.usStates.length > 0 && (
                <Command.Group>
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-tertiary font-medium border-b border-white/5">
                    US States
                  </p>
                  {groupedJurisdictions.usStates.map((jurisdiction) => (
                    <JurisdictionItem
                      key={jurisdiction.code}
                      jurisdiction={jurisdiction}
                      onSelect={() => handleSelect(jurisdiction)}
                    />
                  ))}
                </Command.Group>
              )}

              {/* Canadian Provinces */}
              {groupedJurisdictions.caProvinces.length > 0 && (
                <Command.Group>
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-tertiary font-medium border-b border-white/5">
                    Canadian Provinces
                  </p>
                  {groupedJurisdictions.caProvinces.map((jurisdiction) => (
                    <JurisdictionItem
                      key={jurisdiction.code}
                      jurisdiction={jurisdiction}
                      onSelect={() => handleSelect(jurisdiction)}
                    />
                  ))}
                </Command.Group>
              )}
            </Command.List>
          )}
        </Command>
      </div>
    );
  }
);

InlineSearch.displayName = 'InlineSearch';

// Individual jurisdiction item with flag
interface JurisdictionItemProps {
  jurisdiction: CountryScore;
  onSelect: () => void;
}

const JurisdictionItem = ({ jurisdiction, onSelect }: JurisdictionItemProps) => {
  const status = getStatus(jurisdiction.score);
  const statusColor = getStatusColor(status);
  const statusLabel = getStatusLabel(status);

  return (
    <Command.Item
      value={`${jurisdiction.name} ${jurisdiction.code}`}
      onSelect={onSelect}
      className="flex items-center gap-2 px-3 py-2 cursor-pointer
                 data-[selected=true]:bg-white/10 hover:bg-white/5 transition-colors"
    >
      <FlagIcon code={jurisdiction.code} size="sm" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-text-primary truncate">{jurisdiction.name}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="text-[10px] px-1.5 py-0.5 rounded"
          style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
        >
          {statusLabel}
        </span>
        <span className="text-xs font-medium text-text-secondary w-6 text-right">
          {Math.round(jurisdiction.score)}
        </span>
      </div>
    </Command.Item>
  );
};

export default InlineSearch;
